/**
 * ESS POS — Shared Reusable UI Component Library
 * Provides consistent enterprise formatting, loading/empty/error states,
 * modals, confirmation dialogs, toasts, CSV export, and S3-ready image upload controls.
 */

export function escapeHtml(str: unknown): string {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function formatCurrency(cents: number | undefined | null, currency = 'KES'): string {
  const safeCents = typeof cents === 'number' && !Number.isNaN(cents) ? cents : 0;
  const amount = safeCents / 100;
  return `${currency} ${amount.toLocaleString('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatDateTime(iso: string | Date | undefined | null): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-KE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTimeShort(iso: string | Date | undefined | null): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-KE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// Loading, Empty, and Error States (Mandatory on every screen)
// ============================================================================

export function renderLoadingState(message = 'Loading workspace data from ESS Engine...'): string {
  return `
    <div class="ess-state-box" role="status" aria-live="polite">
      <div class="ess-spinner"></div>
      <div class="ess-state-title">${escapeHtml(message)}</div>
      <div class="ess-state-subtitle">Verifying tenant session &amp; fetching authoritative records</div>
    </div>
  `;
}

export function renderEmptyState(params: {
  title: string;
  description: string;
  actionLabel?: string;
  actionId?: string;
}): string {
  return `
    <div class="ess-state-box ess-empty-state">
      <div class="ess-state-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="3" y="4" width="18" height="16" rx="2"></rect>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <line x1="8" y1="15" x2="16" y2="15"></line>
        </svg>
      </div>
      <div class="ess-state-title">${escapeHtml(params.title)}</div>
      <div class="ess-state-subtitle">${escapeHtml(params.description)}</div>
      ${
        params.actionLabel && params.actionId
          ? `<button type="button" class="ess-btn ess-btn-primary" id="${escapeHtml(params.actionId)}" style="margin-top:12px;">
              ${escapeHtml(params.actionLabel)}
            </button>`
          : ''
      }
    </div>
  `;
}

export function renderErrorState(params: {
  title?: string;
  message: string;
  retryButtonId?: string;
}): string {
  return `
    <div class="ess-state-box ess-error-state" role="alert">
      <div class="ess-state-icon ess-state-icon-danger">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <div class="ess-state-title">${escapeHtml(params.title || 'Operation Could Not Be Completed')}</div>
      <div class="ess-state-subtitle">${escapeHtml(params.message)}</div>
      ${
        params.retryButtonId
          ? `<button type="button" class="ess-btn ess-btn-secondary" id="${escapeHtml(params.retryButtonId)}" style="margin-top:12px;">
              Retry Request
            </button>`
          : ''
      }
    </div>
  `;
}

// ============================================================================
// Toast Notification System
// ============================================================================

export function showToast(params: {
  title: string;
  message?: string;
  type?: 'success' | 'warning' | 'danger' | 'info';
  durationMs?: number;
}) {
  let container = document.getElementById('ess-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'ess-toast-container';
    container.className = 'ess-toast-container';
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  const type = params.type || 'info';
  toast.className = `ess-toast ess-toast-${type}`;
  toast.setAttribute('role', 'status');

  toast.innerHTML = `
    <div class="ess-toast-indicator"></div>
    <div class="ess-toast-body">
      <div class="ess-toast-title">${escapeHtml(params.title)}</div>
      ${params.message ? `<div class="ess-toast-msg">${escapeHtml(params.message)}</div>` : ''}
    </div>
    <button type="button" class="ess-toast-close" aria-label="Dismiss notification">&times;</button>
  `;

  const closeBtn = toast.querySelector('.ess-toast-close');
  const removeToast = () => {
    toast.classList.add('ess-toast-hiding');
    window.setTimeout(() => toast.remove(), 200);
  };

  closeBtn?.addEventListener('click', removeToast);
  container.appendChild(toast);

  window.setTimeout(removeToast, params.durationMs ?? 4200);
}

// ============================================================================
// Accessible Modal & Confirmation Dialog System
// ============================================================================

export function openModal(params: {
  title: string;
  subtitle?: string;
  bodyHtml: string;
  footerHtml?: string;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  onMount?: (modalRoot: HTMLElement, close: () => void) => void;
}): () => void {
  const existing = document.getElementById('ess-active-modal-backdrop');
  if (existing) existing.remove();

  const backdrop = document.createElement('div');
  backdrop.id = 'ess-active-modal-backdrop';
  backdrop.className = 'ess-modal-backdrop';

  const sizeClass = `ess-modal-${params.width || 'md'}`;

  backdrop.innerHTML = `
    <div class="ess-modal ${sizeClass}" role="dialog" aria-modal="true" aria-labelledby="ess-modal-heading">
      <div class="ess-modal-header">
        <div>
          <h3 id="ess-modal-heading" class="ess-modal-title">${escapeHtml(params.title)}</h3>
          ${params.subtitle ? `<p class="ess-modal-subtitle">${escapeHtml(params.subtitle)}</p>` : ''}
        </div>
        <button type="button" class="ess-modal-close-btn" id="ess-modal-close-x" aria-label="Close dialog">&times;</button>
      </div>
      <div class="ess-modal-body">
        ${params.bodyHtml}
      </div>
      ${params.footerHtml ? `<div class="ess-modal-footer">${params.footerHtml}</div>` : ''}
    </div>
  `;

  const close = () => {
    window.removeEventListener('keydown', escHandler);
    backdrop.remove();
  };

  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };

  window.addEventListener('keydown', escHandler);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  document.body.appendChild(backdrop);
  backdrop.querySelector('#ess-modal-close-x')?.addEventListener('click', close);

  if (params.onMount) {
    params.onMount(backdrop, close);
  }

  return close;
}

export function confirmDialog(params: {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => Promise<void> | void;
}) {
  openModal({
    title: params.title,
    width: 'sm',
    bodyHtml: `<p style="margin:0;color:var(--ess-text-secondary);line-height:1.55;">${escapeHtml(params.message)}</p>`,
    footerHtml: `
      <button type="button" class="ess-btn ess-btn-secondary" id="ess-confirm-cancel">Cancel</button>
      <button type="button" class="ess-btn ${params.danger ? 'ess-btn-danger' : 'ess-btn-primary'}" id="ess-confirm-proceed">
        ${escapeHtml(params.confirmLabel || 'Confirm Action')}
      </button>
    `,
    onMount: (root, close) => {
      root.querySelector('#ess-confirm-cancel')?.addEventListener('click', close);
      const proceedBtn = root.querySelector('#ess-confirm-proceed') as HTMLButtonElement | null;
      proceedBtn?.addEventListener('click', async () => {
        if (!proceedBtn) return;
        proceedBtn.disabled = true;
        proceedBtn.textContent = 'Processing...';
        try {
          await params.onConfirm();
          close();
        } catch (err: any) {
          showToast({
            title: 'Action Failed',
            message: err?.message || 'Unable to complete action',
            type: 'danger',
          });
          proceedBtn.disabled = false;
          proceedBtn.textContent = params.confirmLabel || 'Confirm Action';
        }
      });
    },
  });
}

// ============================================================================
// S3 / Object-Storage Media Uploader Component (Camera + Gallery + Progress)
// ============================================================================

export function renderImageUploadControl(controlId: string, label = 'Product / Menu Image'): string {
  return `
    <div class="ess-upload-box" id="${escapeHtml(controlId)}-wrapper">
      <label class="ess-label">${escapeHtml(label)} <span class="ess-badge ess-badge-neutral">S3 / R2 Object Storage</span></label>
      <div class="ess-upload-dropzone" id="${escapeHtml(controlId)}-dropzone">
        <input
          type="file"
          id="${escapeHtml(controlId)}-input"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          style="display:none;"
        />
        <div id="${escapeHtml(controlId)}-preview-area" class="ess-upload-preview-area">
          <div class="ess-upload-placeholder">
            <strong>Capture Photo or Select Image</strong>
            <span>Supports mobile camera &amp; gallery • PNG, JPG, WEBP up to 5 MB</span>
          </div>
        </div>
        <div class="ess-upload-actions">
          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="${escapeHtml(controlId)}-select-btn">
            Camera / Gallery
          </button>
          <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" id="${escapeHtml(controlId)}-cancel-btn" style="display:none;">
            Cancel Upload
          </button>
          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="${escapeHtml(controlId)}-retry-btn" style="display:none;">
            Retry
          </button>
        </div>
        <div class="ess-upload-progress-wrap" id="${escapeHtml(controlId)}-progress-wrap" style="display:none;">
          <div class="ess-upload-progress-bar" id="${escapeHtml(controlId)}-progress-bar" style="width:0%"></div>
        </div>
        <div class="ess-upload-feedback" id="${escapeHtml(controlId)}-feedback"></div>
      </div>
    </div>
  `;
}

export function bindImageUploadControl(
  root: ParentNode,
  controlId: string,
  onUploaded: (mediaUrl: string) => void
) {
  const input = root.querySelector(`#${controlId}-input`) as HTMLInputElement | null;
  const selectBtn = root.querySelector(`#${controlId}-select-btn`) as HTMLButtonElement | null;
  const cancelBtn = root.querySelector(`#${controlId}-cancel-btn`) as HTMLButtonElement | null;
  const retryBtn = root.querySelector(`#${controlId}-retry-btn`) as HTMLButtonElement | null;
  const previewArea = root.querySelector(`#${controlId}-preview-area`) as HTMLElement | null;
  const progressWrap = root.querySelector(`#${controlId}-progress-wrap`) as HTMLElement | null;
  const progressBar = root.querySelector(`#${controlId}-progress-bar`) as HTMLElement | null;
  const feedback = root.querySelector(`#${controlId}-feedback`) as HTMLElement | null;

  let activeTimer: number | null = null;
  let lastFile: File | null = null;

  const startUpload = (file: File) => {
    lastFile = file;
    if (feedback) feedback.textContent = '';
    if (retryBtn) retryBtn.style.display = 'none';

    // Validate file type and size (max 5MB)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      if (feedback) {
        feedback.textContent = 'Invalid file format. Please choose JPG, PNG, or WEBP.';
        feedback.style.color = 'var(--ess-danger-text)';
      }
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      if (feedback) {
        feedback.textContent = 'File exceeds 5 MB limit. Please compress or select a smaller photo.';
        feedback.style.color = 'var(--ess-danger-text)';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (previewArea && typeof reader.result === 'string') {
        previewArea.innerHTML = `<img src="${reader.result}" alt="Upload preview" class="ess-upload-thumb" />`;
      }
    };
    reader.readAsDataURL(file);

    // Simulate signed object storage upload progress with cancellation support
    if (progressWrap) progressWrap.style.display = 'block';
    if (cancelBtn) cancelBtn.style.display = 'inline-flex';
    let pct = 15;
    if (progressBar) progressBar.style.width = `${pct}%`;

    activeTimer = window.setInterval(() => {
      pct += 30;
      if (progressBar) progressBar.style.width = `${Math.min(100, pct)}%`;
      if (pct >= 100) {
        if (activeTimer) window.clearInterval(activeTimer);
        activeTimer = null;
        if (cancelBtn) cancelBtn.style.display = 'none';
        const safeSlug = file.name.toLowerCase().replace(/[^a-z0-9.]/g, '-');
        const signedMediaRef = `https://media.easysmartsystem.com/tenants/s3-objects/${Date.now()}-${safeSlug}`;
        if (feedback) {
          feedback.textContent = `Uploaded to Object Storage (${Math.round(file.size / 1024)} KB)`;
          feedback.style.color = 'var(--ess-success-text)';
        }
        onUploaded(signedMediaRef);
      }
    }, 180);
  };

  selectBtn?.addEventListener('click', () => input?.click());
  input?.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) startUpload(file);
  });

  cancelBtn?.addEventListener('click', () => {
    if (activeTimer) {
      window.clearInterval(activeTimer);
      activeTimer = null;
    }
    if (progressBar) progressBar.style.width = '0%';
    if (progressWrap) progressWrap.style.display = 'none';
    if (cancelBtn) cancelBtn.style.display = 'none';
    if (retryBtn && lastFile) retryBtn.style.display = 'inline-flex';
    if (feedback) {
      feedback.textContent = 'Upload cancelled.';
      feedback.style.color = 'var(--ess-warning-text)';
    }
  });

  retryBtn?.addEventListener('click', () => {
    if (lastFile) startUpload(lastFile);
  });
}

// ============================================================================
// CSV Export Helper
// ============================================================================

export function exportRowsToCsv(filename: string, headers: string[], rows: Array<Array<string | number>>) {
  const escapeCsvCell = (val: string | number) => {
    const s = String(val ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const lines = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((r) => r.map(escapeCsvCell).join(',')),
  ];

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  showToast({
    title: 'Report Exported',
    message: `Downloaded ${filename}`,
    type: 'success',
  });
}

